import config from "@/config";
import { isNumeric } from "@/core/string";
import { Scope } from "@/shared/hooks/useAuth/types";

import { AccessRight } from "./user";

export enum Permissions {
    ViewInformation = "canViewInformation",
    ViewInformationDetail = "canViewInformationDetail",
    ViewRisks = "canViewRisks",
    ViewRisksDetails = "canViewRisksDetails",
    ViewContracts = "canViewContracts",
    ViewContractsDetails = "canViewContractsDetails",
    ViewContractActivities = "canAccessContractActivity",
    ViewClaims = "canViewClaims",
    ViewClaimsDetails = "canViewClaimsDetails",
    ViewClaimActivities = "canAccessClaimActivity",
    ViewClosedClaim = "canViewClosedClaims",
    ViewPolicyholderActivities = "canAccessPolicyholderActivity",
    ViewInsuredGoodActivities = "canAccessInsuredGoodActivity",
    ViewLinkedDocuments = "canViewLinkedDocuments",
    ViewInvoices = "canViewInvoices",
    ViewInvoicePdf = "canViewInvoicePdf",
    AccessConversations = "canAccessConversation",
    CommunicateSalary = "canCommunicateSalary",
    DeclareLiability10 = "canDeclareLiability10",
    GetGreenCard = "canGetGreenCard",
    AccessAddFamilyMember = "canAccessAddFamilyMember",
    AccessDeclareClaim = "canAccessDeclareClaim",
    CanDeclareSchoolClaim = "canDeclareSchoolClaim",
    AccessCustomerForm = "canAccessCustomerForm",
    AccessChangeAddress = "canAccessChangeAddress",
    AccessLastPayment = "canAccessLastPayment",
    AccessEditInformationDetails = "canAccessEditInformationDetails",
    AccessContractManager = "accessContractManager",
    AccessSalesRep = "accessSalesRep",
    AccessTariffication = "canAccessTariffication",
    ViewClaimsPayments = "canViewClaimsPayments",
    ViewClaimManager = "canViewClaimManager",
    ViewGeneralConditions = "hasContractTermsAndConditions",
    ViewSalesRepresentative = "canViewSalesRepresentative",
    ViewManager = "canViewManager",
}

export enum UserState {
    PENDING = "pending",
    UNKNOWN = "unknown",
    PUSH = "push",
    PULL = "pull",
    ACCESS = "access",
}

export class Identity {
    id: string;
    email: string;
    name: string;
    language: string;
    officeId: string;
    itsmeLogin: boolean;
    accessRights: AccessRight[];
    accessToken: string;
    currentBrokerId: string;
    scope?: Scope;
    policyHolderId: number;
    accessControlListId: number;

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    constructor(arg: any) {
        Object.assign(this, arg);
    }

    get isAuthenticated(): boolean {
        return !!this.accessToken;
    }

    isScopedLogin(): boolean {
        return !!this.scope;
    }

    isScope(scope: Scope | Scope[]): boolean {
        if (!this.scope) return false;
        if (Array.isArray(scope)) return scope.includes(this.scope);
        return this.scope === scope;
    }

    hasGlobalPermission(permission: Permissions): boolean {
        if (!this.accessRights || this.accessRights.length === 0) return false;

        const accessRights = this.accessRights.find((item) => item.policyHolderLinked === "Global");

        if (!accessRights) return false;

        return accessRights[permission];
    }

    hasPermission(permission: Permissions, policyHolderId: number | string = null): boolean {
        if (!this.accessRights || this.accessRights.length === 0) return false;

        const accessRights = this.getAccessRightFor(policyHolderId);

        if (!accessRights) return false;
        return accessRights[permission];
    }

    someHasPermission(permission: Permissions, policyHolderIds: (number | string)[]): boolean {
        let isAllowed = false;
        for (const policyholderId of policyHolderIds) {
            if (this.hasPermission(permission, policyholderId)) {
                isAllowed = true;
                break;
            }
        }
        return isAllowed;
    }

    getAccessRightFor(policyholderId: number | string): AccessRight {
        return this.accessRights?.find((item) => item.policyHolderId == policyholderId) || {};
    }

    getIbpAclId(policyHolderId: number | string | null | undefined): number | undefined {
        if (isNumeric(policyHolderId)) {
            return this.getAccessRightFor(policyHolderId)?.accessControlListId;
        }
        return undefined;
    }
}

export const anonymousUser = new Identity({
    name: "anonymous",
    language: config.FALLBACK_LANGUAGE,
    accessRights: [],
});
